import React, { createContext, useContext, useState, useCallback, useMemo } from 'react';
import { Alert, AlertContextType } from '../types/alert';

const AlertContext = createContext<AlertContextType>(null);

export const AlertProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [alerts, setAlerts] = useState<Alert[]>([]);

    const showAlert = useCallback((alert: Omit<Alert, 'id'>): string | number => {
        if (!alert || typeof alert !== 'object') {
            console.error('AlertContext: showAlert requires an alert object');
            return 0;
        }

        if (!alert.message) {
            console.error('AlertContext: alert must have a message property');
            return 0;
        }

        const id = Date.now() + Math.random();
        const newAlert: Alert = {
            type: 'info',
            dismissible: false,
            autoHide: 5000,
            ...alert,
            id
        };
        setAlerts(prev => [...prev, newAlert]);
        return id;
    }, []);

    const hideAlert = useCallback((id: string | number) => {
        if (!id) {
            console.error('AlertContext: hideAlert requires an alert ID');
            return;
        }

        setAlerts(prev => prev.filter(alert => alert.id !== id));
    }, []);

    const clearAll = useCallback(() => {
        setAlerts([]);
    }, []);

    const hasAlerts = alerts.length > 0;
    const alertCount = alerts.length;

    const contextValue: AlertContextType = useMemo(() => ({
        alerts,
        showAlert,
        hideAlert,
        clearAll,
        hasAlerts,
        alertCount
    }), [alerts, showAlert, hideAlert, clearAll]);

    return (
        <AlertContext.Provider value={contextValue}>
            {children}
        </AlertContext.Provider>
    );
};

export const useAlert = () => {
    const context = useContext(AlertContext);

    if (context === null) {
        console.warn(
            'AlertContext: Using default context. Components should be wrapped with AlertProvider.'
        );
    }
    return context;
};

export const useAlertHelpers = () => {
    const { showAlert } = useAlert();

    return {
        showSuccess: (message: string, options = {}) => showAlert({ ...options, type: 'success', message }),
        showError: (message: string, options = {}) => showAlert({ ...options, type: 'error', message }),
        showWarning: (message: string, options = {}) => showAlert({ ...options, type: 'warning', message }),
        showInfo: (message: string, options = {}) => showAlert({ ...options, type: 'info', message })
    };
};